<template>
  <nav class="PkiToolbar">
    <div class="PkiToolbar__searchBox">
      <InputSearch
        class="PkiToolbar__searchInput"
        :isDisabled="isLoading"
        placeholder="Search"
        :value="searchTerm"
        @input="triggerSearch"
      />

      <IconButton
        class="PkiToolbar__filterToggle"
        hasNoBackground
        icon="caretDown"
        iconPosition="right"
        :isDisabled="isLoading"
        :scale="0.75"
        text="Filters"
        type="informational"
        @click="toggleFilters"
      />
    </div>

    <div class="PkiToolbar__actions">
      <DropdownMenu
        :align="columnsDropdownAlign"
        class="PkiToolbar__dropdown"
        :isDisabled="isLoading"
        :items="dropdownItems"
        selectable
        :toggleProps="{
          hasNoBackground: true,
          icon: 'caretDown',
          iconPosition: 'right',
          text: 'Columns',
          type: 'informational',
          scale: 0.75,
        }"
        @checkboxChange="handleColumnSelect"
      >
        <template #header="{ isExpanded }">
          <div class="PkiToolbar__dropdownHeader">
            <HeadingExtraSmall class="PkiToolbar__dropdownHeading">
              Columns
            </HeadingExtraSmall>

            <Button
              class="PkiToolbar__btnRestoreColumns"
              hasNoBackground
              :tabindex="isExpanded ? 0 : -1"
              text="Restore Defaults"
              type="informational"
              @click="restoreColumns"
            />
          </div>

          <ParagraphBody data-test-id="PkiToolbar__ColumnCount">
            <b>{{ numSelectedColumns }} Selected</b> ({{ dropdownItems.length }} Max)
          </ParagraphBody>
        </template>
      </DropdownMenu>

      <IconButton
        hasNoBackground
        icon="fileDownload"
        :isDisabled="isLoading || !hasSelectedCertificates"
        text="Export"
        type="informational"
        @click="handleExport"
      />

      <Button
        hasOutline
        :isDisabled="isLoading || !hasSelectedCertificates"
        text="Revoke"
        type="error"
        @click="handleRevoke"
      />
    </div>
  </nav>
</template>

<script>
import {
  Button,
  HeadingExtraSmall,
  IconButton,
  InputSearch,
  ParagraphBody,
} from '@jumpcloud/ui-components';
import DropdownMenu from 'client/Pki/components/DropdownMenu.vue';

export default {
  name: 'PkiToolbar',

  components: {
    Button,
    DropdownMenu,
    HeadingExtraSmall,
    IconButton,
    InputSearch,
    ParagraphBody,
  },

  props: {
    columns: {
      type: Array,
      required: true,
    },

    hasSelectedCertificates: Boolean,

    isLoading: Boolean,

    searchTerm: {
      type: String,
      default: '',
    },
  },

  data() {
    return {
      columnsDropdownAlign: 'left',
    };
  },

  computed: {
    dropdownItems() {
      return this.columns.reduce((items, column) => {
        if (column.dataFieldName !== 'icon') {
          items.push({
            props: {
              label: column.label,
              value: column.visible,
            },
          });
        }

        return items;
      }, []);
    },

    numSelectedColumns() {
      return this.dropdownItems.reduce((num, item) => num + Number(item.props.value), 0);
    },
  },

  mounted() {
    this.resizeObserver = new ResizeObserver(this.handleResize);

    this.resizeObserver.observe(this.$el);
  },

  beforeDestroy() {
    this.resizeObserver.disconnect();
  },

  methods: {
    handleColumnSelect(selected, _, i) {
      this.columns[i].visible = selected;

      this.$emit('columnSelect', this.columns);
    },

    handleConfigureList() {
      this.$emit('configureList');
    },

    handleExport() {
      this.$emit('export');
    },

    handleResize() {
      this.columnsDropdownAlign = window.innerWidth >= 768 ? 'right' : 'left';
    },

    handleRevoke() {
      this.$emit('revoke');
    },

    restoreColumns() {
      this.$emit('restoreColumns');
    },

    toggleFilters() {
      // @todo
    },

    triggerSearch(value) {
      this.$emit('search', value);
    },
  },
};
</script>

<style scoped>
.PkiToolbar {
  align-items: center;
  display: flex;
  flex-direction: column;
  gap: var(--jc-spacer-small);
  padding-bottom: var(--jc-spacer-small);
}

.PkiToolbar__searchBox {
  align-items: center;
  display: flex;
  flex: 1;
  position: relative;
  width: 100%;
}

.PkiToolbar__searchInput {
  flex: 1;
  position: relative;
  z-index: 0;
}

.PkiToolbar__searchInput >>> input {
  padding-right: 90px;
}

.PkiToolbar .PkiToolbar__filterToggle {
  position: absolute;
  right: var(--jc-spacer-small);
  z-index: 1;
}

.PkiToolbar__actions {
  align-items: center;
  display: flex;
  gap: var(--jc-spacer-medium);
  justify-content: flex-start;
  padding: 0 var(--jc-spacer);
  width: 100%;
}

.PkiToolbar__dropdownHeader {
  align-items: center;
  display: flex;
  flex-wrap: wrap;
  gap: var(--jc-spacer-medium);
  justify-content: space-between;
  margin-bottom: var(--jc-spacer);
  min-width: 300px;
}

.PkiToolbar .PkiToolbar__dropdownHeading {
  margin-bottom: 0;
}

.PkiToolbar__btnRestoreColumns {
  white-space: nowrap;
}

@media all and (min-width: 768px) {
  .PkiToolbar {
    flex-direction: row;
    gap: var(--jc-spacer-large);
    justify-content: space-between;
    margin-bottom: var(--jc-spacer);

    /* extra padding to match ItemList since it inexplicably has right padding */
    padding-right: var(--jc-spacer);
  }

  .PkiToolbar__searchBox {
    max-width: 640px;
    width: auto;
  }

  .PkiToolbar__actions {
    justify-content: flex-end;
    padding: 0;
    width: auto;
  }
}
</style>
